/** Subset of tenant row used for SP-027 CSS variables (from `GET /workspaces`). */
export type WorkspaceThemeRow = {
  themePrimary: string | null;
  themePrimaryLight: string | null;
  themePrimaryBorder: string | null;
  themeFont: string | null;
};

/** Tailwind/shadcn `--primary` uses space-separated HSL components (no `hsl()` wrapper). */
function hexToHslSpaceSeparated(hex: string): string | null {
  const m = /^#?([0-9a-f]{6}|[0-9a-f]{3})$/i.exec(hex.trim());
  if (!m?.[1]) return null;
  let h = m[1];
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let hDeg = 0;
  const l = (max + min) / 2;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        hDeg = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        hDeg = ((b - r) / d + 2) / 6;
        break;
      default:
        hDeg = ((r - g) / d + 4) / 6;
        break;
    }
  }
  const H = Math.round(hDeg * 360);
  const S = Math.round(s * 1000) / 10;
  const L = Math.round(l * 1000) / 10;
  return `${H} ${S}% ${L}%`;
}

const OVERRIDDEN_ROOT_KEYS = [
  '--color-primary',
  '--color-primary-light',
  '--color-primary-border',
  '--font-family',
  '--primary',
  '--sidebar-primary',
] as const;

/** Remove tenant theme inline overrides (e.g. on logout). */
export function clearWorkspaceTheme(): void {
  const root = document.documentElement;
  for (const k of OVERRIDDEN_ROOT_KEYS) {
    root.style.removeProperty(k);
  }
}

/** Apply SP-027 tokens from `GET /workspaces` row to `:root`. */
export function applyWorkspaceThemeFromRow(row: WorkspaceThemeRow | null): void {
  if (typeof document === 'undefined') return;
  if (!row) return;
  const root = document.documentElement;
  if (row.themePrimary) {
    root.style.setProperty('--color-primary', row.themePrimary);
    const hsl = hexToHslSpaceSeparated(row.themePrimary);
    if (hsl) {
      root.style.setProperty('--primary', hsl);
      root.style.setProperty('--sidebar-primary', hsl);
    }
  }
  if (row.themePrimaryLight) {
    root.style.setProperty('--color-primary-light', row.themePrimaryLight);
  }
  if (row.themePrimaryBorder) {
    root.style.setProperty('--color-primary-border', row.themePrimaryBorder);
  }
  if (row.themeFont) {
    root.style.setProperty('--font-family', row.themeFont);
  }
}
